import React, { useState, useMemo } from 'react';
import { Button, Container, Grid, Paper, Typography, Slider, TextField, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import api from '../hooks/useApi';
import { NavLink, useLoaderData } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import {requireSuperAdmin} from '../auth/guards';

const initialFilterState = {
    numberOfPlaces: [0, 100],
    townFilters: [],
    classificationFilters: []
};

export async function loader({ request, params }) {
    const authResult = await requireSuperAdmin({request, params});
    if (authResult instanceof Response) return authResult;
    const response = await api.get('/api/hotels/group/owner/get/');
    const users = response.data?.users;
    if (!Array.isArray(users)) {
        throw new Response('Некоректна відповідь списку власників', {status: 502});
    }
    const initialCards = users.map((card) => ({
        ...card,
        cities: Array.isArray(card.cities) ? card.cities : [],
        classifications: Array.isArray(card.classifications) ? card.classifications : [],
    }));
    return { initialCards };
}

export default function Owners() {
    const { initialCards } = useLoaderData();
    const [filter, setFilter] = useState(initialFilterState);
    const [cards] = useState(initialCards);
    const [townFilter, setTownFilter] = useState('');
    const [classificationFilter, setClassificationFilter] = useState('');

    const [sliderValue, setSliderValue] = useState(initialFilterState.numberOfPlaces);


    // const handleFilterChange = (event, newValue) => {
    //     const name = event.target.name;
    //     setFilter({
    //         ...filter,
    //         [name]: newValue
    //     });
    // };

    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const handleTownFilterChange = (event) => {
        const town = event.target.name;
        const isChecked = event.target.checked;
        let updatedTowns = [...filter.townFilters];
        if (isChecked) {
            updatedTowns.push(town);
        } else {
            updatedTowns = updatedTowns.filter(item => item !== town);
        }
        setFilter({
            ...filter,
            townFilters: updatedTowns
        });
    };

    const handleSliderChangeCommitted = () => {
        setFilter(prevFilter => ({
            ...prevFilter,
            numberOfPlaces: sliderValue
        }));
    };

    const handleClassificationFilterChange = (event) => {
        const classification = event.target.name;
        const isChecked = event.target.checked;
        let updatedClassifications = [...filter.classificationFilters];
        if (isChecked) {
            updatedClassifications.push(classification);
        } else {
            updatedClassifications = updatedClassifications.filter(item => item !== classification);
        }
        setFilter({
            ...filter,
            classificationFilters: updatedClassifications
        });
    };

    const filteredCards = cards.filter(card => {
        const isWithinPlacesRange = card.obj_children_count >= filter.numberOfPlaces[0] && card.obj_children_count <= filter.numberOfPlaces[1];

        const matchesTownFilter = filter.townFilters.length === 0 || card.cities.some(town => filter.townFilters.includes(town));

        const matchesClassificationFilter = filter.classificationFilters.length === 0 || card.classifications.some(classification => filter.classificationFilters.includes(classification));
        const result = isWithinPlacesRange && (matchesTownFilter || matchesClassificationFilter);
        //console.log(result)
        return result;
    });

    const renderedCards = useMemo(() =>
        filteredCards.map(card => (
            <Grid item key={card.id} xs={12} sm={6} md={4} lg={4} >
                <Paper sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%', p: 2,
                    borderRadius: '10px',
                    justifyContent: 'space-around'
                }}>
                    <Grid container spacing={1.5} style={{ marginBottom: '15px' }}>
                        <Grid item container>
                            <Typography variant="h6">{card.name}</Typography>
                        </Grid>
                        <Grid item container>
                            <Typography>{card.phone}</Typography>
                        </Grid>
                        <Grid item container>
                            <Typography>{card.email}</Typography>
                        </Grid>
                        <Grid item container>
                            <Typography>Кількість місць: {card.obj_children_count}</Typography>
                        </Grid>
                        {card.cities?.length > 0 && (<Grid item container>
                            <Typography>Міста розташування: {card.cities.join(', ')}</Typography>
                        </Grid>)}
                        {card.classifications?.length > 0 && (<Grid item container>
                            <Typography>Класифікація: {card.classifications.join(', ')}</Typography>
                        </Grid>)}
                    </Grid>
                    <Grid container style={{ marginTop: 'auto' }} justifyContent="flex-end">
                        <Button variant="contained" component={NavLink} to={`/calendar?owners=${card.id}`} color="primary">До об'єктів</Button>
                    </Grid>
                </Paper>
            </Grid>
        ))
        , [filteredCards])
    if (cards.length === 0) {
        return <Container style={{paddingTop: '20px'}}><PageTitle title="Власники"/><Typography>Партнерів поки немає.</Typography></Container>;
    }
    return (
        <Container style={{ paddingTop: '20px' }}>
            <PageTitle title="Власники" />
            <Grid container spacing={3} >
                {/* Filter Column */}
                <Grid item md={3} sm={6} xs={12} sx={{height:'fit-content'}}>
                    <Paper sx={{ p: 3, borderRadius: '10px' }}>
                        <Typography variant="subtitle1">Кількість місць об'єктів</Typography>
                        <Grid container>
                            <Grid item xs={2}>
                                <Typography variant="body2">{sliderValue[0]}</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Slider
                                    name="numberOfPlaces"
                                    value={sliderValue}
                                    onChange={handleSliderChange}
                                    onChangeCommitted={handleSliderChangeCommitted}
                                    min={0}
                                    max={100}
                                    valueLabelDisplay="auto"
                                    aria-labelledby="range-slider"
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2" style={{ textAlign: 'right', width: '100%' }}>{sliderValue[1]}</Typography>
                            </Grid>
                        </Grid>

                        {filter && (<>
                            <Divider style={{ margin: '20px 0' }} orientation='horizontal' />

                            <Typography variant="subtitle1">Місто розташування</Typography>
                            <TextField
                                label="Пошук"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={townFilter}
                                onChange={(event) => { setTownFilter(event.target.value) }}
                            />
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                <FormGroup>
                                    {Array.from(new Set(cards.flatMap(card => card.cities)))
                                        .filter(town => town.toLowerCase().includes(townFilter.toLowerCase()))
                                        .map(town => (
                                            <FormControlLabel
                                                key={town}
                                                control={<Checkbox checked={filter.townFilters.includes(town)} onChange={handleTownFilterChange} name={town} />}
                                                label={town}
                                            />
                                        ))}
                                </FormGroup>
                            </div></>)}

                        <Divider style={{ margin: '20px 0' }} orientation='horizontal' />

                        <Typography variant="subtitle1">Класифікація</Typography>
                        <TextField
                            label="Пошук"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={classificationFilter}
                            onChange={(event) => { setClassificationFilter(event.target.value) }}
                        />
                        <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                            <FormGroup>
                                {Array.from(new Set(cards.flatMap(card => card.classifications)))
                                    .filter(classification => classification.toLowerCase().includes(classificationFilter.toLowerCase()))
                                    .map(classification => (
                                        <FormControlLabel
                                            key={classification}
                                            control={<Checkbox checked={filter.classificationFilters.includes(classification)} onChange={handleClassificationFilterChange} name={classification} />}
                                            label={classification}
                                        />
                                    ))}

                            </FormGroup>
                        </div>
                    </Paper>
                </Grid>

                {/* Cards Column */}
                <Grid item md={9} xs={12}>
                    <Grid container spacing={3}>
                        {renderedCards}
                    </Grid>
                </Grid>
            </Grid>
        </Container >
    );
}
